import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  LocalOffer as LocalOfferIcon,
  LockOutlined as LockOutlinedIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import {
  tienePermisosModulo,
  puedeAccionFacturacion,
} from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import { useProductosMaterialContext } from 'context';
import tiposDocumentosData from 'utils/tiposDocumentos.json';

const PROGRAMA_DESCUENTOS_GENERALES = 'descuentosgenerales';

const DescuentosGenerales = forwardRef(function DescuentosGenerales({ embedded = false }, ref) {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = tienePermisosModulo(roleDetalles, PROGRAMA_DESCUENTOS_GENERALES);
  const puedeModificar = puedeAccionFacturacion(
    roleDetalles,
    PROGRAMA_DESCUENTOS_GENERALES,
    'modificar'
  );
  const puedeBorrar = puedeAccionFacturacion(
    roleDetalles,
    PROGRAMA_DESCUENTOS_GENERALES,
    'borrar'
  );

  const [tiposDocumento] = useState(() => {
    const documentos = [];
    tiposDocumentosData.forEach((categoria) => {
      if (categoria.documentosaplicarimpuestos && Array.isArray(categoria.documentosaplicarimpuestos)) {
        documentos.push(...categoria.documentosaplicarimpuestos);
      }
    });
    return documentos;
  });

  const [tipoDocumentoSeleccionado, setTipoDocumentoSeleccionado] = useState('');
  const [reglas, setReglas] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [reglaEditando, setReglaEditando] = useState(null);

  const [nombreDescuento, setNombreDescuento] = useState('');
  const [aplicarSoloPorEncimaMonto, setAplicarSoloPorEncimaMonto] = useState('N');
  const [montoMinimo, setMontoMinimo] = useState('');
  const [porcentaje, setPorcentaje] = useState('0');
  const [activa, setActiva] = useState('S');
  const [guardando, setGuardando] = useState(false);

  const cargarReglas = useCallback(async () => {
    if (!puedeLeer || !selectedCompany?.db_empresa) return;
    if (!tipoDocumentoSeleccionado) {
      setReglas([]);
      return;
    }
    setCargando(true);
    try {
      const response = await fetch(servidor + '/api/facturabackend_obtenerreglasdescuentosgenerales', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tipo_documento: tipoDocumentoSeleccionado,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setReglas(data.reglas || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al cargar reglas de descuentos');
      }
    } catch {
      showError('Error de conexión al cargar reglas');
    } finally {
      setCargando(false);
    }
  }, [
    puedeLeer,
    selectedCompany,
    usuarioLog,
    claveLog,
    servidor,
    tipoDocumentoSeleccionado,
    showError,
  ]);

  useEffect(() => {
    cargarReglas();
  }, [cargarReglas]);

  const handleAbrirDialogo = (regla = null) => {
    if (!puedeModificar) {
      showError('No tiene permiso para modificar reglas de descuento.');
      return;
    }
    if (!regla && !tipoDocumentoSeleccionado) {
      showError('Seleccione un tipo de documento para crear una regla de descuento.');
      return;
    }
    if (regla) {
      setModoEdicion(true);
      setReglaEditando(regla);
      setNombreDescuento(regla.nombre_descuento || '');
      setAplicarSoloPorEncimaMonto(regla.aplicar_solo_por_encima_monto || 'N');
      setMontoMinimo(regla.monto_minimo != null ? String(regla.monto_minimo) : '');
      setPorcentaje(regla.porcentaje != null ? String(regla.porcentaje) : '0');
      setActiva(regla.activa || 'S');
    } else {
      setModoEdicion(false);
      setReglaEditando(null);
      setNombreDescuento('');
      setAplicarSoloPorEncimaMonto('N');
      setMontoMinimo('');
      setPorcentaje('0');
      setActiva('S');
    }
    setDialogoAbierto(true);
  };

  useImperativeHandle(ref, () => ({
    abrirNuevaRegla: () => handleAbrirDialogo(),
  }));

  const handleCerrarDialogo = () => {
    setDialogoAbierto(false);
    setReglaEditando(null);
    setNombreDescuento('');
    setAplicarSoloPorEncimaMonto('N');
    setMontoMinimo('');
    setPorcentaje('0');
    setActiva('S');
  };

  const handleGuardar = async () => {
    if (!puedeModificar) {
      showError('No tiene permiso para guardar reglas de descuento.');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    if (!nombreDescuento.trim()) {
      showError('El nombre del descuento es requerido');
      return;
    }
    if (aplicarSoloPorEncimaMonto === 'S') {
      const monto = parseFloat(montoMinimo);
      if (isNaN(monto) || monto < 0) {
        showError('Indique un monto mínimo válido cuando aplica solo por encima de un monto');
        return;
      }
    }
    const porcentajeNum = parseFloat(porcentaje);
    if (isNaN(porcentajeNum) || porcentajeNum < 0 || porcentajeNum > 100) {
      showError('El porcentaje debe ser un número entre 0 y 100');
      return;
    }

    setGuardando(true);
    try {
      const reglaData = {
        tipo_documento: tipoDocumentoSeleccionado,
        nombre_descuento: nombreDescuento.trim(),
        aplicar_solo_por_encima_monto: aplicarSoloPorEncimaMonto,
        monto_minimo: aplicarSoloPorEncimaMonto === 'S' ? parseFloat(montoMinimo) : null,
        porcentaje: porcentajeNum,
        activa,
      };
      if (modoEdicion && reglaEditando) {
        reglaData.id = reglaEditando.id;
      }

      const response = await fetch(servidor + '/api/facturabackend_guardarregladescuentogeneral', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          regla_descuento: reglaData,
        }),
      });

      if (response.ok) {
        showSuccess('Regla de descuento guardada correctamente');
        handleCerrarDialogo();
        cargarReglas();
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al guardar la regla');
      }
    } catch {
      showError('Error de conexión al guardar');
    } finally {
      setGuardando(false);
    }
  };

  const handleEliminar = async (regla) => {
    if (!puedeBorrar) {
      showError('No tiene permiso para eliminar reglas de descuento.');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    if (!window.confirm(`¿Eliminar la regla "${regla.nombre_descuento}"?`)) return;
    try {
      const response = await fetch(servidor + '/api/facturabackend_eliminarregladescuentogeneral', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          regla_id: regla.id,
        }),
      });
      if (response.ok) {
        showSuccess('Regla eliminada correctamente');
        cargarReglas();
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al eliminar');
      }
    } catch {
      showError('Error de conexión al eliminar');
    }
  };

  const cardShellSx = {
    borderRadius: 2.5,
    border: `1px solid ${FP.color.border}`,
    bgcolor: FP.color.cardBg,
    boxShadow: FP.shadow.soft,
    overflow: 'hidden',
  };

  const filterHeaderSx = {
    px: { xs: 2, md: 3 },
    py: 1.6,
    borderBottom: `1px solid ${FP.color.border}`,
    bgcolor: '#FAFBFE',
    display: 'flex',
    alignItems: 'center',
    gap: 1.2,
  };

  const cellHeadSx = {
    fontWeight: 700,
    fontSize: '0.75rem',
    color: FP.color.textSecondary,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    display: 'block',
    mb: 0.5,
  };

  if (!puedeLeer) {
    return (
      <Box component={embedded ? 'section' : 'main'} sx={embedded ? {} : { minHeight: '100vh', bgcolor: FP.color.surface }}>
        {!embedded && (
          <PageHeader
            title="Descuentos generales"
            subtitle={
              nombreNegocio ? `${nombreNegocio} · Reglas por tipo de documento` : 'Reglas por tipo de documento'
            }
          />
        )}
        <Box sx={{ p: embedded ? 0 : { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «descuentosgenerales» en roles para configurar descuentos generales.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component={embedded ? 'section' : 'main'}
      sx={embedded ? {} : { minHeight: '100vh', bgcolor: FP.color.surface }}
    >
      {!embedded && (
        <PageHeader
          title="Descuentos generales"
          subtitle={
            nombreNegocio ? `${nombreNegocio} · Reglas por tipo de documento` : 'Reglas por tipo de documento'
          }
        />
      )}

      <Box sx={{ p: embedded ? 0 : { xs: 1.5, sm: 2, md: 3 } }}>
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LocalOfferIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Tipo de documento
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.92rem', lineHeight: 1.5, mb: 2 }}>
              Defina porcentajes de descuento general que se aplican automáticamente al subtotal, opcionalmente solo por encima de un monto mínimo.
            </Typography>
            <FormControl fullWidth sx={{ maxWidth: 360, ...fpInputSx }}>
              <InputLabel id="desc-gral-tipo-doc" shrink>
                Tipo de documento
              </InputLabel>
              <Select
                labelId="desc-gral-tipo-doc"
                value={tipoDocumentoSeleccionado}
                onChange={(e) => setTipoDocumentoSeleccionado(e.target.value)}
                label="Tipo de documento"
                displayEmpty
              >
                <MenuItem value="">
                  <em>Seleccione el tipo de documento</em>
                </MenuItem>
                {tiposDocumento.map((doc) => (
                  <MenuItem key={doc} value={doc}>
                    {doc}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>

        {!tipoDocumentoSeleccionado ? (
          <Card elevation={0} sx={cardShellSx}>
            <CardContent>
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <LocalOfferIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  Seleccione un tipo de documento
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                  Para configurar las reglas de descuento general aplicables a las facturas.
                </Typography>
              </Box>
            </CardContent>
          </Card>
        ) : (
          <Card elevation={0} sx={cardShellSx}>
            <Box
              sx={{
                px: { xs: 2, md: 3 },
                py: 1.6,
                borderBottom: `1px solid ${FP.color.border}`,
                bgcolor: '#FAFBFE',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
                Reglas para: {tipoDocumentoSeleccionado}
              </Typography>
              {!embedded && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleAbrirDialogo()}
                disabled={!puedeModificar || !usuarioLog}
                sx={{
                  height: 38,
                  bgcolor: FP.color.navy,
                  color: '#FFFFFF',
                  borderRadius: 1.4,
                  textTransform: 'none',
                  fontWeight: 700,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                  '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
                }}
              >
                Agregar regla
              </Button>
              )}
            </Box>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
              {cargando ? (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                  <Typography sx={{ color: FP.color.textSecondary, fontWeight: 600 }}>Cargando…</Typography>
                </Box>
              ) : reglas.length === 0 ? (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                  <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                  <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                    No hay reglas para este documento
                  </Typography>
                  <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                    Use «{embedded ? 'Nueva regla' : 'Agregar regla'}» para crear la primera.
                  </Typography>
                </Box>
              ) : (
                <TableContainer
                  component={Paper}
                  elevation={0}
                  sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto' }}
                >
                  <Table sx={{ tableLayout: 'fixed', width: '100%', minWidth: { xs: 760, sm: 920 } }}>
                    <colgroup>
                      {['26%', '22%', '14%', '12%', '12%', '14%'].map((w, i) => (
                        <col key={i} style={{ width: w }} />
                      ))}
                    </colgroup>
                    <TableBody>
                      <TableRow
                        sx={{
                          position: 'sticky',
                          top: 0,
                          zIndex: 2,
                          '& td': {
                            border: 'none',
                            borderBottom: `2px solid ${FP.color.border}`,
                            bgcolor: FP.color.surfaceAlt,
                          },
                        }}
                      >
                        <TableCell>
                          <Typography component="span" sx={cellHeadSx}>
                            Nombre del descuento
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                            Aplicación
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                            Monto mínimo
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'right', width: '100%' }}>
                            Porcentaje
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                            Estado
                          </Typography>
                        </TableCell>
                        <TableCell className="no-print">
                          <Typography component="span" sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}>
                            Acciones
                          </Typography>
                        </TableCell>
                      </TableRow>
                      {reglas.map((regla) => (
                        <TableRow key={regla.id} hover>
                          <TableCell
                            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            title={regla.nombre_descuento}
                          >
                            {regla.nombre_descuento}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={
                                regla.aplicar_solo_por_encima_monto === 'S'
                                  ? 'Solo por encima de monto'
                                  : 'Todas las facturas'
                              }
                              size="small"
                              sx={{
                                bgcolor:
                                  regla.aplicar_solo_por_encima_monto === 'S'
                                    ? FP.color.accentBlueSoft
                                    : FP.color.surfaceAlt,
                                color:
                                  regla.aplicar_solo_por_encima_monto === 'S'
                                    ? FP.color.accentBlue
                                    : FP.color.textSecondary,
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {regla.aplicar_solo_por_encima_monto === 'S' && regla.monto_minimo != null
                              ? `$${Number(regla.monto_minimo).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                              : '—'}
                          </TableCell>
                          <TableCell align="right">
                            {regla.porcentaje != null ? `${Number(regla.porcentaje).toFixed(2)}%` : '0%'}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={regla.activa === 'S' ? 'Activa' : 'Inactiva'}
                              size="small"
                              sx={{
                                bgcolor: regla.activa === 'S' ? FP.color.successSoft : FP.color.surfaceAlt,
                                color: regla.activa === 'S' ? FP.color.successDark : FP.color.textSecondary,
                                fontWeight: 600,
                              }}
                            />
                          </TableCell>
                          <TableCell align="center" className="no-print">
                            <IconButton
                              size="small"
                              onClick={() => handleAbrirDialogo(regla)}
                              disabled={!puedeModificar || !usuarioLog}
                              sx={{
                                color: FP.color.accentBlue,
                                '&:hover': { bgcolor: FP.color.accentBlueSoft },
                                '&.Mui-disabled': { color: FP.color.textMuted },
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            {puedeBorrar && (
                              <IconButton
                                size="small"
                                onClick={() => handleEliminar(regla)}
                                disabled={!usuarioLog}
                                sx={{
                                  color: FP.color.danger,
                                  '&:hover': { bgcolor: FP.color.dangerSoft },
                                  '&.Mui-disabled': { color: FP.color.textMuted },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        )}
      </Box>

      <Dialog
        open={dialogoAbierto}
        onClose={handleCerrarDialogo}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            display: 'flex',
            alignItems: 'center',
            gap: 1.2,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.2,
              bgcolor: FP.color.accentBlueSoft,
              color: FP.color.accentBlue,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LocalOfferIcon sx={{ fontSize: 18 }} />
          </Box>
          <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
            {modoEdicion ? 'Editar regla de descuento' : 'Nueva regla de descuento'}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
          <TextField
            fullWidth
            label="Nombre del descuento"
            value={nombreDescuento}
            onChange={(e) => setNombreDescuento(e.target.value)}
            required
            disabled={!puedeModificar || !usuarioLog}
            helperText="Nombre que identifica esta regla de descuento"
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2, mt: 1, ...fpInputSx }}
          />

          <Alert
            severity="info"
            icon={false}
            sx={{
              mb: 2,
              borderRadius: 2,
              border: `1px solid ${FP.color.border}`,
              bgcolor: FP.color.surfaceAlt,
              color: FP.color.textPrimary,
            }}
          >
            <Typography
              sx={{
                fontSize: '0.78rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: FP.color.textSecondary,
                mb: 0.5,
              }}
            >
              ¿Cuándo aplicar?
            </Typography>
            <RadioGroup
              row
              value={aplicarSoloPorEncimaMonto}
              onChange={(e) => setAplicarSoloPorEncimaMonto(e.target.value)}
            >
              <FormControlLabel
                value="N"
                control={<Radio sx={{ '&.Mui-checked': { color: FP.color.accentBlue } }} />}
                label="A todas las facturas"
                disabled={!puedeModificar || !usuarioLog}
              />
              <FormControlLabel
                value="S"
                control={<Radio sx={{ '&.Mui-checked': { color: FP.color.accentBlue } }} />}
                label="Solo por encima de un monto"
                disabled={!puedeModificar || !usuarioLog}
              />
            </RadioGroup>
          </Alert>

          {aplicarSoloPorEncimaMonto === 'S' && (
            <TextField
              fullWidth
              label="Monto mínimo"
              type="number"
              value={montoMinimo}
              onChange={(e) => setMontoMinimo(e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              disabled={!puedeModificar || !usuarioLog}
              helperText="El descuento aplica cuando el total de la factura sea mayor o igual a este monto"
              InputLabelProps={{ shrink: true }}
              sx={{ mb: 2, ...fpInputSx }}
            />
          )}

          <TextField
            fullWidth
            label="Porcentaje de descuento"
            type="number"
            value={porcentaje}
            onChange={(e) => setPorcentaje(e.target.value)}
            inputProps={{ min: 0, max: 100, step: 0.01 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
            disabled={!puedeModificar || !usuarioLog}
            helperText="Porcentaje a aplicar (0-100). Use 0 si solo desea registrar la regla por nombre."
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2, ...fpInputSx }}
          />

          <FormControl fullWidth sx={fpInputSx}>
            <InputLabel id="desc-gral-estado" shrink>
              Estado
            </InputLabel>
            <Select
              labelId="desc-gral-estado"
              value={activa}
              onChange={(e) => setActiva(e.target.value)}
              label="Estado"
              disabled={!puedeModificar || !usuarioLog}
            >
              <MenuItem value="S">Activa</MenuItem>
              <MenuItem value="N">Inactiva</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button
            onClick={handleCerrarDialogo}
            startIcon={<CloseIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleGuardar}
            disabled={guardando || !puedeModificar || !usuarioLog}
            sx={{
              textTransform: 'none',
              borderRadius: 1.4,
              fontWeight: 700,
              bgcolor: FP.color.navy,
              color: '#FFFFFF',
              boxShadow: 'none',
              '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
            }}
          >
            {modoEdicion ? 'Modificar' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default DescuentosGenerales;
